import {Switch} from '@headlessui/react';
import React, {useState} from 'react';

import LoadingSpinner from 'src/components/LoadingSpinner';
import {useTranslations} from 'src/components/TranslationsContext';
import {
  useCreateNotificationPreferenceMutation,
  useNotificationPreferences,
  useUpdateNotificationPreferenceMutation,
} from 'src/hooks/useNotificationPreferences';
import {useProjectsWithSites} from 'src/hooks/useProject';
import {SeriesType} from 'src/lib/api/project';
import {
  NOTIFICATION_SCOPE_ALL,
  NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
} from 'src/lib/constants';

import CollapsibleNotificationsPanel from './CollapsibleNotificationPanel';
import ExpansionButton from './ExpandNotificationSectionButton';

export default function NewForecastNotifications() {
  const {t} = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);
  const {notificationPreferences: newForecastPrefs} = useNotificationPreferences(
    NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST
  );
  const {projects, isLoaded: areProjectsLoaded} = useProjectsWithSites();

  const projectsWithShortTermSites = projects?.filter(
    (p) => p.sites?.filter((s) => s.seriesTypes.includes(SeriesType.FlowShortTerm)).length
  );

  const updateNotificationPref = useUpdateNotificationPreferenceMutation();
  const createNotificationPref = useCreateNotificationPreferenceMutation();

  // Count total sites to determine if we need the "Show More" button
  const totalSites =
    projectsWithShortTermSites?.reduce((count, project) => {
      return (
        count +
        (project.sites?.filter((site) => site.seriesTypes.includes(SeriesType.FlowShortTerm))
          .length || 0)
      );
    }, 0) || 0;

  const conciseViewSiteLimit = 5;
  // Get sites with short term forecasts across all projects,
  // potentially truncated to 5 total sites to conserve vertical space.
  const getShortTermSitesPerProject = () => {
    if (!projectsWithShortTermSites) return [];

    const result = [];
    let siteCount = 0;

    for (const project of projectsWithShortTermSites) {
      if (!isExpanded && siteCount >= conciseViewSiteLimit) {
        // We've already reached our limit
        break;
      }
      const filteredSites =
        project.sites?.filter((site) => site.seriesTypes.includes(SeriesType.FlowShortTerm)) || [];

      const projectData = {
        ...project,
        sites: isExpanded
          ? filteredSites
          : filteredSites.slice(0, Math.max(0, conciseViewSiteLimit - siteCount)),
      };

      if (projectData.sites.length > 0) {
        result.push(projectData);
        siteCount += projectData.sites.length;
      }
    }

    return result;
  };

  const shortTermSitesPerProject = getShortTermSitesPerProject();

  return (
    <div className="md:grid md:grid-cols-3 md:gap-6">
      <div className="md:col-span-1">
        <div className="px-4 sm:px-0">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t('newForecastNotifications')}
          </h3>
          <div className="mt-1 text-sm text-gray-600">{t('newForecastDescription')}</div>
        </div>
      </div>
      <div className="mt-5 md:mt-0 md:col-span-2">
        <CollapsibleNotificationsPanel title={t('enrollInNewForecastNotifications')}>
          <div className="space-y-6">
            <>
              <div className="px-4 py-5 bg-white space-y-6">
                <div className="mt-5 md:mt-0 md:col-span-2">
                  {areProjectsLoaded ? (
                    shortTermSitesPerProject.length ? (
                      <>
                        {shortTermSitesPerProject.map((p) => {
                          return (
                            <div className="mb-4" key={p.id}>
                              <div className="font-bold">{p.name}</div>
                              <table className="w-full">
                                <colgroup>
                                  <col span={1} />
                                  <col span={1} style={{width: '44px'}} />
                                  <col span={1} style={{width: '44px'}} />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th scope="col"></th>
                                    <th scope="col">0Z</th>
                                    <th scope="col">12Z</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.sites?.map((site, i) => {
                                    const sitePrefs = newForecastPrefs?.find(
                                      (np) =>
                                        np.properties?.hfProjectId === p.id &&
                                        np.properties?.siteId === site.id
                                    );

                                    const isEnabled0Z = !!sitePrefs?.properties['0Z'];
                                    const isEnabled12Z = !!sitePrefs?.properties['12Z'];

                                    return (
                                      <tr key={i}>
                                        <td>{site.name}</td>
                                        <td className="align-center">
                                          <Switch
                                            checked={isEnabled0Z}
                                            onChange={(isEnabled: boolean) => {
                                              if (sitePrefs) {
                                                updateNotificationPref.mutate({
                                                  projectId: p.id,
                                                  id: sitePrefs.id,
                                                  properties: {
                                                    ...sitePrefs.properties,
                                                    hfProjectId: p.id,
                                                    siteId: site.id,
                                                    type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                    '0Z': isEnabled,
                                                  },
                                                  type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                  scope: NOTIFICATION_SCOPE_ALL, // Scope isn't used for new forecast notifications, so we just set it to all.
                                                });
                                              } else {
                                                createNotificationPref.mutate({
                                                  projectId: p.id,
                                                  properties: {
                                                    hfProjectId: p.id,
                                                    siteId: site.id,
                                                    type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                    '0Z': isEnabled,
                                                  },
                                                  ccEmails: [],
                                                  type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                  scope: NOTIFICATION_SCOPE_ALL,
                                                });
                                              }
                                            }}
                                            className={`${
                                              isEnabled0Z ? 'bg-blue-600' : 'bg-gray-200'
                                            } relative inline-flex h-6 w-11 items-center rounded-full`}
                                          >
                                            <span className="sr-only">
                                              {t('enable0ZForecastNotifications')}
                                            </span>
                                            <span
                                              className={`${
                                                isEnabled0Z ? 'translate-x-6' : 'translate-x-1'
                                              } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                            />
                                          </Switch>
                                        </td>
                                        <td className="align-center">
                                          <Switch
                                            checked={isEnabled12Z}
                                            onChange={(isEnabled: boolean) => {
                                              if (sitePrefs) {
                                                updateNotificationPref.mutate({
                                                  projectId: p.id,
                                                  id: sitePrefs.id,
                                                  properties: {
                                                    ...sitePrefs.properties,
                                                    hfProjectId: p.id,
                                                    siteId: site.id,
                                                    type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                    '12Z': isEnabled,
                                                  },
                                                  type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                  scope: NOTIFICATION_SCOPE_ALL, // Scope isn't used for new forecast notifications, so we just set it to all.
                                                });
                                              } else {
                                                createNotificationPref.mutate({
                                                  projectId: p.id,
                                                  properties: {
                                                    hfProjectId: p.id,
                                                    siteId: site.id,
                                                    type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                    '12Z': isEnabled,
                                                  },
                                                  ccEmails: [],
                                                  type: NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
                                                  scope: NOTIFICATION_SCOPE_ALL,
                                                });
                                              }
                                            }}
                                            className={`${
                                              isEnabled12Z ? 'bg-blue-600' : 'bg-gray-200'
                                            } relative inline-flex h-6 w-11 items-center rounded-full`}
                                          >
                                            <span className="sr-only">
                                              {t('enable12ZForecastNotifications')}
                                            </span>
                                            <span
                                              className={`${
                                                isEnabled12Z ? 'translate-x-6' : 'translate-x-1'
                                              } inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                            />
                                          </Switch>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                        {totalSites > conciseViewSiteLimit && (
                          <ExpansionButton
                            isExpanded={isExpanded}
                            setIsExpanded={setIsExpanded}
                            expandText={t('showAllSites')}
                            collapseText={t('showLess')}
                          />
                        )}
                      </>
                    ) : (
                      <p>{t('noProjectsMessage')}</p>
                    )
                  ) : (
                    <div className="flex justify-center">
                      <LoadingSpinner />
                    </div>
                  )}
                </div>
              </div>
            </>
          </div>
        </CollapsibleNotificationsPanel>
      </div>
    </div>
  );
}
